import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import Sidebar from "../../components/Sidebar";
import PCMBadge from "../../components/PCMBadge";
import PCMFloorsEditor from "../../components/PCMFloorsEditor";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Mail,
  Shield,
  Eye,
  CreditCard,
  CheckCircle,
  Clock,
  XCircle,
  Key,
  Unlock,
  Zap,
  FolderOpen,
  Building2,
} from "lucide-react";

import { API } from "../../utils/api";

const PCM_TYPES = ["Analyseur", "Empathique", "Persévérant", "Énergiseur", "Promoteur", "Imagineur"];
const PAYMENT_PLANS = [
  { value: "full", label: "Paiement intégral" },
  { value: "2x", label: "Paiement en 2 fois" },
  { value: "3x", label: "Paiement en 3 fois" },
];

const AdminUsers = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);
  const [isUnlockModulesOpen, setIsUnlockModulesOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [userModulesStatus, setUserModulesStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    pcm_base: "",
    pcm_phase: "",
    pcm_floors: {},
    role: "user",
    payment_plan: "full",
    total_amount: "",
    access_status: "pending",
    program_id: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchPrograms = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/programs`, {
        headers,
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.filter(p => p.is_active));
      }
    } catch (error) {
      console.error("Error fetching programs:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/users`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/admin/users`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...formData,
          pcm_base: formData.pcm_base || null,
          pcm_phase: formData.pcm_phase || null,
          total_amount: formData.total_amount ? parseFloat(formData.total_amount) : null,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Utilisateur créé");
        setIsCreateOpen(false);
        setFormData({ 
          name: "", email: "", password: "", pcm_base: "", pcm_phase: "", 
          role: "user", payment_plan: "full", total_amount: "", access_status: "pending",
          program_id: ""
        });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la création");
      }
    } catch (error) {
      toast.error("Erreur lors de la création");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // Prepare pcm_floors - only include if any value is set
      const hasPcmFloors = Object.values(formData.pcm_floors || {}).some(v => v > 0);

      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          name: formData.name,
          pcm_base: formData.pcm_base || null,
          pcm_phase: formData.pcm_phase || null,
          pcm_floors: hasPcmFloors ? formData.pcm_floors : null,
          role: formData.role,
          enrolled_program_id: formData.program_id || undefined,
          onboarding_completed: formData.pcm_base && formData.pcm_phase ? true : undefined,
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Utilisateur mis à jour");
        setIsEditOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Supprimer cet utilisateur ?")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers,
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Utilisateur supprimé");
        fetchUsers();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      pcm_base: user.pcm_base || "",
      pcm_phase: user.pcm_phase || "",
      pcm_floors: user.pcm_floors || {},
      role: user.role,
      program_id: user.enrolled_program_id || "",
    });
    setIsEditOpen(true);
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setIsResetPasswordOpen(true);
  };

  const openUnlockModulesDialog = async (user) => {
    setSelectedUser(user);
    setUserModulesStatus(null);
    setIsUnlockModulesOpen(true);
    
    // Fetch user's module status
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/admin/users/${user.user_id}/modules-status`, {
        headers,
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserModulesStatus(data);
      }
    } catch (error) {
      console.error("Error fetching modules status:", error);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    setResetPasswordLoading(true);
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/reset-password`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ new_password: newPassword }),
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(`Mot de passe réinitialisé pour ${selectedUser.name}`);
        setIsResetPasswordOpen(false);
        setSelectedUser(null);
        setNewPassword("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de la réinitialisation");
      }
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleUnlockAllModules = async () => {
    if (!selectedUser) return;
    
    setUnlockLoading(true);
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/unlock-modules`, {
        method: "POST",
        headers,
        body: JSON.stringify({ unlock_all: true, module_numbers: [] }),
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh modules status
        openUnlockModulesDialog(selectedUser);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du déblocage");
      }
    } catch (error) {
      toast.error("Erreur lors du déblocage");
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleUnlockSpecificModule = async (moduleNumber) => {
    if (!selectedUser) return;
    
    setUnlockLoading(true);
    
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/unlock-modules`, {
        method: "POST",
        headers,
        body: JSON.stringify({ unlock_all: false, module_numbers: [moduleNumber] }),
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh modules status
        openUnlockModulesDialog(selectedUser);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du déblocage");
      }
    } catch (error) {
      toast.error("Erreur lors du déblocage");
    } finally {
      setUnlockLoading(false);
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />

      <main className="flex-1 p-6 lg:p-8 lg:ml-64" data-testid="admin-users">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D] mb-2">
              Gestion des utilisateurs
            </h1>
            <p className="text-slate-600">{users.length} utilisateurs enregistrés</p>
          </div>

          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                data-testid="create-user-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouvel utilisateur
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Créer un utilisateur</DialogTitle>
                <DialogDescription>
                  Créez un nouvel accès pour un participant
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom complet</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="create-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="create-user-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mot de passe</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    data-testid="create-user-password"
                  />
                </div>

                {/* Programme Selection */}
                <div className="space-y-2">
                  <Label>Programme</Label>
                  <Select
                    value={formData.program_id}
                    onValueChange={(v) => setFormData({ ...formData, program_id: v })}
                  >
                    <SelectTrigger data-testid="create-user-program">
                      <SelectValue placeholder="Sélectionner un programme" />
                    </SelectTrigger>
                    <SelectContent>
                      {programs.map((program) => (
                        <SelectItem key={program.program_id} value={program.program_id}>
                          <span className="flex items-center">
                            <span
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: program.color || '#FF6B35' }}
                            />
                            {program.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">
                    {programs.length === 0 ? "Aucun programme actif" : "Le programme par défaut sera utilisé si non sélectionné"}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Base PCM</Label>
                    <Select
                      value={formData.pcm_base}
                      onValueChange={(v) => setFormData({ ...formData, pcm_base: v })}
                    >
                      <SelectTrigger data-testid="create-user-base">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {PCM_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phase PCM</Label>
                    <Select
                      value={formData.pcm_phase}
                      onValueChange={(v) => setFormData({ ...formData, pcm_phase: v })}
                    >
                      <SelectTrigger data-testid="create-user-phase">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {PCM_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Payment Options */}
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium text-slate-700 mb-3 flex items-center">
                    <CreditCard className="w-4 h-4 mr-2 text-[#FF6B35]" />
                    Options de paiement
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Plan de paiement</Label>
                      <Select
                        value={formData.payment_plan}
                        onValueChange={(v) => setFormData({ ...formData, payment_plan: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_PLANS.map((plan) => (
                            <SelectItem key={plan.value} value={plan.value}>
                              {plan.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Montant total (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.total_amount}
                        onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                        placeholder="Ex: 890"
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-3">
                  <Label>Statut d&apos;accès initial</Label>
                    <Select
                      value={formData.access_status}
                      onValueChange={(v) => setFormData({ ...formData, access_status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-2 text-yellow-500" />
                            En attente de paiement
                          </span>
                        </SelectItem>
                        <SelectItem value="active">
                          <span className="flex items-center">
                            <CheckCircle className="w-3 h-3 mr-2 text-green-500" />
                            Accès activé (déjà payé)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-[#101E4D] hover:bg-[#0A1330]"
                  data-testid="create-user-submit"
                >
                  Créer l&apos;utilisateur
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <Card className="mb-6 bg-white">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-50 border-slate-200"
                data-testid="search-users-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card className="bg-white">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                      Utilisateur
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                      Programme
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                      Profil PCM
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                      Rôle
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.user_id}
                      className="border-b border-slate-50 hover:bg-slate-50/50"
                      data-testid={`user-row-${user.user_id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {user.picture ? (
                            <img
                              src={user.picture}
                              alt={user.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-[#101E4D] flex items-center justify-center">
                              <span className="text-white font-medium">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-[#101E4D]">{user.name}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {user.enrolled_program_name ? (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-[#FF6B35]/10 text-[#FF6B35] rounded-lg">
                            <FolderOpen className="w-3 h-3 mr-1" />
                            {user.enrolled_program_name}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">Non inscrit</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-2">
                          {user.pcm_base && <PCMBadge type={user.pcm_base} size="sm" />}
                          {user.pcm_phase && <PCMBadge type={user.pcm_phase} size="sm" />}
                          {!user.pcm_base && !user.pcm_phase && (
                            <span className="text-sm text-slate-400">Non configuré</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {user.role === "admin" ? (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-lg">
                            <Shield className="w-3 h-3 mr-1" />
                            Admin
                          </span>
                        ) : (
                          <span className="text-sm text-slate-600">Utilisateur</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {user.onboarding_completed ? (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-lg">
                            Actif
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-lg">
                            En attente
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`user-actions-${user.user_id}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(user)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Modifier
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openResetPasswordDialog(user)}>
                              <Key className="w-4 h-4 mr-2" />
                              Réinitialiser MDP
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openUnlockModulesDialog(user)}>
                              <Unlock className="w-4 h-4 mr-2" />
                              Débloquer modules
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteUser(user.user_id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun utilisateur trouvé</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier l&apos;utilisateur</DialogTitle>
              <DialogDescription>
                Mettez à jour les informations et le profil PCM
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom complet</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formData.email} disabled className="bg-slate-100" />
              </div>

              {/* PCM Floors Editor - Replaces simple Base/Phase selectors */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-[#FF6B35]" />
                  <span className="text-sm font-semibold text-slate-700">Configuration PCM</span>
                </div>
                <PCMFloorsEditor
                  pcmFloors={formData.pcm_floors}
                  pcmBase={formData.pcm_base}
                  pcmPhase={formData.pcm_phase}
                  onChange={(floors, base, phase) => {
                    setFormData({ 
                      ...formData, 
                      pcm_floors: floors, 
                      pcm_base: base, 
                      pcm_phase: phase 
                    });
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Rôle</Label>
                <Select
                  value={formData.role}
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                >
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utilisateur</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Programme Selection */}
              <div className="space-y-2">
                <Label>Programme inscrit</Label>
                <Select
                  value={formData.program_id}
                  onValueChange={(v) => setFormData({ ...formData, program_id: v })}
                >
                  <SelectTrigger data-testid="edit-user-program">
                    <SelectValue placeholder="Sélectionner un programme" />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map((program) => (
                      <SelectItem key={program.program_id} value={program.program_id}>
                        <span className="flex items-center">
                          <span
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: program.color || '#FF6B35' }}
                          />
                          {program.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#101E4D] hover:bg-[#0A1330]"
                data-testid="edit-user-submit"
              >
                Enregistrer les modifications
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={isResetPasswordOpen} onOpenChange={setIsResetPasswordOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Key className="w-5 h-5 mr-2 text-[#FF6B35]" />
                Réinitialiser le mot de passe
              </DialogTitle>
              <DialogDescription>
                Définir un nouveau mot de passe pour {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-[#101E4D]">{selectedUser?.name}</p>
                <p className="text-sm text-slate-500">{selectedUser?.email}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">Nouveau mot de passe</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 caractères"
                  required
                  minLength={6}
                  data-testid="reset-password-input"
                />
              </div>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsResetPasswordOpen(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={resetPasswordLoading || newPassword.length < 6}
                  className="flex-1 bg-[#FF6B35] hover:bg-[#E85D2A]"
                  data-testid="reset-password-submit"
                >
                  {resetPasswordLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    "Réinitialiser"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Unlock Modules Dialog */}
        <Dialog open={isUnlockModulesOpen} onOpenChange={setIsUnlockModulesOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Unlock className="w-5 h-5 mr-2 text-[#7FD858]" />
                Débloquer les modules (Mode test)
              </DialogTitle>
              <DialogDescription>
                Débloquer les modules pour {selectedUser?.name} sans attendre le délai de 12 jours
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* User info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-[#101E4D]">{selectedUser?.name}</p>
                <p className="text-sm text-slate-500">{selectedUser?.email}</p>
              </div>

              {/* Quick action - Unlock all */}
              <Button
                onClick={handleUnlockAllModules}
                disabled={unlockLoading}
                className="w-full bg-[#7FD858] hover:bg-[#6BC847] text-[#101E4D] font-semibold"
                data-testid="unlock-all-modules-btn"
              >
                {unlockLoading ? (
                  <div className="w-4 h-4 border-2 border-[#101E4D] border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Débloquer TOUS les modules
              </Button>

              {/* Modules status */}
              <div className="border rounded-lg divide-y">
                <div className="p-3 bg-slate-50">
                  <p className="text-sm font-medium text-slate-700">État des modules</p>
                </div>
                {userModulesStatus ? (
                  userModulesStatus.modules_status.map((module) => (
                    <div key={module.module_number} className="p-3 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          module.is_completed 
                            ? "bg-green-500 text-white" 
                            : module.is_unlocked 
                            ? "bg-blue-500 text-white"
                            : "bg-slate-200 text-slate-500"
                        }`}>
                          {module.module_number}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#101E4D]">{module.title}</p>
                          <p className="text-xs text-slate-500">
                            {module.is_completed 
                              ? "✅ Terminé" 
                              : module.is_unlocked 
                              ? "🔓 Débloqué" 
                              : module.days_remaining !== null
                              ? `🔒 Dans ${module.days_remaining} jour(s)`
                              : "🔒 Module précédent requis"}
                          </p>
                        </div>
                      </div>
                      {!module.is_unlocked && !module.is_completed && module.module_number > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnlockSpecificModule(module.module_number)}
                          disabled={unlockLoading}
                          className="border-[#7FD858] text-[#7FD858] hover:bg-[#7FD858] hover:text-white"
                        >
                          <Unlock className="w-3 h-3 mr-1" />
                          Débloquer
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <div className="w-6 h-6 border-2 border-slate-300 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-sm text-slate-500 mt-2">Chargement...</p>
                  </div>
                )}
              </div>

              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note :</strong> Cette fonctionnalité est destinée aux utilisateurs tests. 
                  Elle permet de parcourir le programme en accéléré sans attendre le délai normal de 12 jours entre chaque module.
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminUsers;
